import React, { createContext, useState, useContext, useEffect, ReactNode } from 'react';
import { useIndustryConfig } from './IndustryConfigContext';

export type AppMode = 'sales' | 'recruiting' | 'onboarding';

interface AppModeContextType {
  mode: AppMode;
  setMode: (mode: AppMode) => void;
  toggleMode: () => void;
}

const AppModeContext = createContext<AppModeContextType | undefined>(undefined);

export const useAppMode = () => {
  const context = useContext(AppModeContext);
  if (!context) {
    throw new Error('useAppMode must be used within AppModeProvider');
  }
  return context;
};

interface AppModeProviderProps {
  children: ReactNode;
}

export const AppModeProvider: React.FC<AppModeProviderProps> = ({ children }) => {
  const { availableModes } = useIndustryConfig();

  // Initialize from localStorage or default to first available mode
  const [mode, setModeState] = useState<AppMode>(() => {
    const savedMode = localStorage.getItem('appMode') as AppMode | null;
    if (savedMode && availableModes.includes(savedMode)) {
      return savedMode;
    }
    return availableModes[0] || 'sales';
  });

  // Auto-switch if current mode becomes unavailable (e.g. industry changed)
  useEffect(() => {
    if (!availableModes.includes(mode)) {
      const fallback = availableModes[0] || 'sales';
      setModeState(fallback);
      localStorage.setItem('appMode', fallback);
    }
  }, [availableModes, mode]);

  const setMode = (newMode: AppMode) => {
    if (availableModes.includes(newMode)) {
      setModeState(newMode);
      localStorage.setItem('appMode', newMode);
    }
  };

  const toggleMode = () => {
    const currentIndex = availableModes.indexOf(mode);
    const newMode = availableModes[(currentIndex + 1) % availableModes.length];
    setMode(newMode);
  };

  // Update document title based on mode
  useEffect(() => {
    const baseTitle = document.title.split(' - ')[0];
    const modeLabels = { sales: 'Sales', recruiting: 'Recruiting', onboarding: 'Onboarding' };
    document.title = `${baseTitle} - ${modeLabels[mode]} Mode`;
  }, [mode]);

  const value = {
    mode,
    setMode,
    toggleMode
  };

  return (
    <AppModeContext.Provider value={value}>
      {children}
    </AppModeContext.Provider>
  );
};

export default AppModeContext;
